import React, { useEffect, useMemo, useState, useCallback } from 'react';
import dataApi, { BatchOption, BatchAssignment } from '../../../../Api/data.api';

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    width: '520px',
    maxWidth: '92vw',
    maxHeight: '88vh',
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(28, 34, 38, 0.98)',
    border: '1px solid rgba(108, 117, 125, 0.4)',
    borderRadius: '10px',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5)',
    overflow: 'hidden',
  },
  header: {
    padding: '1rem 1.25rem',
    borderBottom: '1px solid rgba(108, 117, 125, 0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: { fontSize: '1rem', color: '#e8e8e8', fontWeight: 600 },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    color: '#8b949e',
    fontSize: '1.2rem',
    cursor: 'pointer',
    lineHeight: 1,
  },
  body: { padding: '1rem 1.25rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.85rem' },
  rangeBox: {
    fontSize: '0.82rem',
    color: '#c5c5c5',
    background: 'rgba(83, 189, 228, 0.1)',
    border: '1px solid rgba(83, 189, 228, 0.3)',
    borderRadius: '6px',
    padding: '0.6rem 0.75rem',
  },
  label: { fontSize: '0.78rem', color: '#8b949e', marginBottom: '0.3rem', display: 'block' },
  filters: { display: 'flex', gap: '0.5rem' },
  input: {
    flex: 1,
    padding: '0.5rem 0.65rem',
    borderRadius: '6px',
    border: '1px solid rgba(108, 117, 125, 0.4)',
    background: 'rgba(20, 24, 28, 0.8)',
    color: '#e8e8e8',
    fontSize: '0.85rem',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
    maxHeight: '280px',
    overflowY: 'auto',
    border: '1px solid rgba(108, 117, 125, 0.2)',
    borderRadius: '6px',
    padding: '0.4rem',
  },
  batchRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.15rem',
    padding: '0.5rem 0.6rem',
    borderRadius: '6px',
    border: '1px solid transparent',
    background: 'rgba(33, 40, 44, 0.6)',
    cursor: 'pointer',
    textAlign: 'left',
  },
  batchRowSelected: {
    border: '1px solid #53bde4',
    background: 'rgba(83, 189, 228, 0.15)',
  },
  batchTitle: { fontSize: '0.86rem', color: '#e8e8e8', fontWeight: 600 },
  batchMeta: { fontSize: '0.74rem', color: '#8b949e' },
  empty: { fontSize: '0.82rem', color: '#6c757d', padding: '1rem', textAlign: 'center' },
  footer: {
    padding: '0.85rem 1.25rem',
    borderTop: '1px solid rgba(108, 117, 125, 0.25)',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
  },
  btn: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    border: '1px solid rgba(108, 117, 125, 0.4)',
    background: 'rgba(33, 40, 44, 0.6)',
    color: '#c5c5c5',
  },
  btnPrimary: {
    border: '1px solid rgba(83, 189, 228, 0.5)',
    background: 'rgba(83, 189, 228, 0.2)',
    color: '#53bde4',
  },
  error: { color: '#ff6b6b', fontSize: '0.8rem' },
};

function toLocalInput(d: Date): string {
  // YYYY-MM-DD in local time
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
}

function formatRange(from: Date, to: Date): string {
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
  };
  return `${from.toLocaleString(undefined, opts)} → ${to.toLocaleString(undefined, opts)}`;
}

interface BatchAssignModalProps {
  datasetId: number;
  range: { from: Date; to: Date };
  onClose: () => void;
  onSaved: (metricCount: number) => void;
}

export function BatchAssignModal({ datasetId, range, onClose, onSaved }: BatchAssignModalProps) {
  const [date, setDate] = useState<string>(toLocalInput(range.from));
  const [search, setSearch] = useState('');
  const [batches, setBatches] = useState<BatchOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadBatches = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await dataApi.getBatches({
        date: date || undefined,
        search: search || undefined,
        take: 200,
      });
      setBatches(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load batches');
    } finally {
      setLoading(false);
    }
  }, [date, search]);

  // Debounced reload on filter changes
  useEffect(() => {
    const t = setTimeout(loadBatches, 250);
    return () => clearTimeout(t);
  }, [loadBatches]);

  const handleSave = async () => {
    if (!selectedBatch) return;
    setSaving(true);
    setError(null);
    try {
      const res = await dataApi.createBatchAssignment(datasetId, {
        batchId: selectedBatch,
        from: range.from.toISOString(),
        to: range.to.toISOString(),
        note: note || undefined,
      });
      onSaved(res.metricCount);
    } catch (err: any) {
      setError(err.message || 'Failed to assign batch');
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <span style={styles.title}>Assign period to batch</span>
          <button style={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={styles.body}>
          <div style={styles.rangeBox}>
            <strong>Selected period:</strong> {formatRange(range.from, range.to)}
          </div>

          <div>
            <span style={styles.label}>Filter batches</span>
            <div style={styles.filters}>
              <input
                style={{ ...styles.input, flex: '0 0 150px' }}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
              <input
                style={styles.input}
                type="text"
                placeholder="Search mix code / product…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div style={styles.list}>
            {loading ? (
              <div style={styles.empty}>Loading batches…</div>
            ) : batches.length === 0 ? (
              <div style={styles.empty}>No batches found. Adjust the date or search.</div>
            ) : (
              batches.map((b) => (
                <button
                  key={b.id}
                  style={{
                    ...styles.batchRow,
                    ...(selectedBatch === b.id ? styles.batchRowSelected : {}),
                  }}
                  onClick={() => setSelectedBatch(b.id)}
                >
                  <span style={styles.batchTitle}>
                    #{b.sequence} · {b.mixCode}
                    {b.productName ? ` — ${b.productName}` : ''}
                  </span>
                  <span style={styles.batchMeta}>
                    {b.productionDate ? new Date(b.productionDate).toLocaleDateString() : 'no date'}
                    {b.ovenName ? ` · ${b.ovenName}` : ''}
                    {b.batchStatus ? ` · ${b.batchStatus}` : ''}
                  </span>
                </button>
              ))
            )}
          </div>

          <div>
            <span style={styles.label}>Note (optional)</span>
            <input
              style={styles.input}
              type="text"
              placeholder="e.g. oven 2 morning run"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          {error && <div style={styles.error}>{error}</div>}
        </div>

        <div style={styles.footer}>
          <button style={styles.btn} onClick={onClose} disabled={saving}>Cancel</button>
          <button
            style={{
              ...styles.btn,
              ...styles.btnPrimary,
              opacity: !selectedBatch || saving ? 0.5 : 1,
              cursor: !selectedBatch || saving ? 'not-allowed' : 'pointer',
            }}
            onClick={handleSave}
            disabled={!selectedBatch || saving}
          >
            {saving ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface BatchAssignmentsListProps {
  assignments: BatchAssignment[];
  colorFor: (id: number) => string;
  onDelete: (id: number) => void;
  deletingId?: number | null;
}

const listStyles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
    background: 'rgba(33, 40, 44, 0.6)',
    border: '1px solid rgba(108, 117, 125, 0.2)',
    borderRadius: '8px',
    padding: '0.75rem',
  },
  heading: { fontSize: '0.85rem', color: '#c5c5c5', fontWeight: 600, marginBottom: '0.25rem' },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.4rem 0.5rem',
    borderRadius: '6px',
    background: 'rgba(20, 24, 28, 0.6)',
  },
  swatch: { width: '12px', height: '12px', borderRadius: '3px', flexShrink: 0 },
  info: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  name: { fontSize: '0.82rem', color: '#e8e8e8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  meta: { fontSize: '0.72rem', color: '#8b949e' },
  del: {
    background: 'transparent',
    border: '1px solid rgba(255, 107, 107, 0.4)',
    color: '#ff6b6b',
    borderRadius: '4px',
    fontSize: '0.72rem',
    padding: '0.25rem 0.5rem',
    cursor: 'pointer',
  },
  empty: { fontSize: '0.78rem', color: '#6c757d' },
};

export function BatchAssignmentsList({ assignments, colorFor, onDelete, deletingId }: BatchAssignmentsListProps) {
  const sorted = useMemo(
    () => [...assignments].sort((a, b) => new Date(a.from).getTime() - new Date(b.from).getTime()),
    [assignments]
  );

  return (
    <div style={listStyles.container}>
      <div style={listStyles.heading}>Assigned periods ({assignments.length})</div>
      {sorted.length === 0 ? (
        <div style={listStyles.empty}>No periods assigned yet.</div>
      ) : (
        sorted.map((a) => (
          <div key={a.id} style={listStyles.row}>
            <span style={{ ...listStyles.swatch, background: colorFor(a.id) }} />
            <div style={listStyles.info}>
              <span style={listStyles.name}>
                {a.batch ? `#${a.batch.sequence} · ${a.batch.mixCode}` : `Batch ${a.batchId}`}
                {a.batch?.productName ? ` — ${a.batch.productName}` : ''}
              </span>
              <span style={listStyles.meta}>
                {formatRange(new Date(a.from), new Date(a.to))} · {a.metricCount} metrics
              </span>
            </div>
            <button
              style={{ ...listStyles.del, opacity: deletingId === a.id ? 0.5 : 1 }}
              onClick={() => onDelete(a.id)}
              disabled={deletingId === a.id}
            >
              {deletingId === a.id ? '…' : 'Remove'}
            </button>
          </div>
        ))
      )}
    </div>
  );
}
